// PJC v10 — galerie : synchronisation variante ↔ photo sans reconstruire le composant, vignettes et compteur.
import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLButtonElement[]} [thumbs] - The thumbnail buttons (PJC gallery).
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller?.abort();
  }

  /**
   * Handles a product select event. When the new response carries the same set of media (the usual case,
   * since every photo stays visible), the gallery is kept and simply moves to the photo of the chosen
   * variant: no re-render, so scroll position, thumbnails and zoom state are preserved. When the set
   * differs (combined listing, hidden variant photos), the gallery is replaced as before.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        if (this.#hasSameMedia(newMediaGallery)) {
          this.#showMedia(newMediaGallery.dataset.selectedMediaId);
          return;
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * @param {HTMLElement} other - The gallery coming from the server response.
   * @returns {boolean} True when both galleries expose exactly the same media.
   */
  #hasSameMedia(other) {
    const ids = this.dataset.mediaIds;
    return Boolean(ids) && ids === other.dataset.mediaIds;
  }

  /**
   * Moves the slideshow to a given media, without animation.
   * @param {string | undefined} mediaId - The media id (slide-id).
   */
  #showMedia(mediaId) {
    if (!mediaId) return;

    this.slideshow?.select({ id: mediaId }, undefined, { animate: false });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Keeps thumbnails and counter in sync with the slideshow.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    const index = event.detail?.index;
    if (typeof index === 'number') this.#syncNav(index);
  };

  /**
   * Updates the current thumbnail (aria-current, roving tabindex), the counter and the thumbnail rail.
   * @param {number} index - The index of the displayed media.
   */
  #syncNav(index) {
    const { thumbs } = this.refs;

    if (thumbs?.length) {
      thumbs.forEach((thumb, i) => {
        const isCurrent = i === index;
        thumb.setAttribute('aria-current', String(isCurrent));
        thumb.tabIndex = isCurrent ? 0 : -1;
      });

      const currentThumb = thumbs[index];
      if (currentThumb) this.#revealThumb(currentThumb);
    }

    const counter = this.querySelector('[data-pjc-current]');
    if (counter) counter.textContent = String(index + 1);
  }

  /**
   * Scrolls the thumbnail rail (never the page) so that a thumbnail is centred.
   * @param {HTMLElement} thumb - The thumbnail button.
   */
  #revealThumb(thumb) {
    const rail = thumb.closest('.pjc-gallery__thumbs');
    if (!(rail instanceof HTMLElement) || rail.scrollWidth <= rail.clientWidth) return;

    const railRect = rail.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    const delta = thumbRect.left - railRect.left - (rail.clientWidth - thumbRect.width) / 2;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    rail.scrollBy({ left: delta, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  /**
   * Selects a media from a thumbnail.
   *
   * @param {number} index - The index of the media to display.
   * @param {Event} [event] - The click event.
   */
  selectMedia(index, event) {
    if (typeof index !== 'number' || !this.slideshow) return;

    this.slideshow.select(index, event);
    this.#syncNav(index);
  }

  /**
   * Arrow / Home / End navigation inside the thumbnail rail (roving tabindex).
   * @param {KeyboardEvent} event - The keydown event.
   */
  thumbKeydown(event) {
    const { thumbs } = this.refs;
    if (!thumbs?.length) return;

    const current = thumbs.findIndex((thumb) => thumb === document.activeElement);
    if (current === -1) return;

    const isRtl = getComputedStyle(this).direction === 'rtl';
    const forward = isRtl ? 'ArrowLeft' : 'ArrowRight';
    const backward = isRtl ? 'ArrowRight' : 'ArrowLeft';

    let target = current;
    if (event.key === forward) target = Math.min(current + 1, thumbs.length - 1);
    else if (event.key === backward) target = Math.max(current - 1, 0);
    else if (event.key === 'Home') target = 0;
    else if (event.key === 'End') target = thumbs.length - 1;
    else return;

    event.preventDefault();
    if (target === current) return;

    this.selectMedia(target);
    thumbs[target]?.focus({ preventScroll: true });
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
